import type { ContainerTareEntry } from "@/types/api";

const DEFAULT_CARRIER = "_DEFAULT";
const FALLBACK_TYPE = "DV";

function normalizeSizeBucket(size: string | null | undefined): string {
  const digits = (size ?? "40").replace(/\D/g, "");
  const n = Number.parseInt(digits, 10);
  if (Number.isFinite(n) && n <= 20) return "20";
  return "40";
}

function normalizeEquipType(type: string | null | undefined): string {
  const t = (type ?? FALLBACK_TYPE).trim().toUpperCase();
  return t || FALLBACK_TYPE;
}

/** Uppercase carrier token for map keys (API rows are already canonical). */
function normalizeCarrierKey(carrier: string): string {
  return carrier.trim().toUpperCase();
}

export type TareLookupSource = "carrier" | "default";

export interface TareLookupResult {
  tareKg: number;
  source: TareLookupSource;
}

/**
 * In-memory tare map aligned with `TareWeightMap` in tideway-core: carrier
 * rows override `_DEFAULT` per (size, type); unknown carriers use defaults.
 */
export class ContainerTareMap {
  private entries = new Map<string, number>();
  private defaults = new Map<string, number>();

  static fromApiRows(rows: ContainerTareEntry[]): ContainerTareMap {
    const map = new ContainerTareMap();
    for (const row of rows) {
      const size = normalizeSizeBucket(row.equipment_size);
      const eqType = normalizeEquipType(row.equipment_type);
      if (row.carrier.toUpperCase() === DEFAULT_CARRIER) {
        map.defaults.set(`${size}:${eqType}`, row.tare_kg);
        continue;
      }
      const carrier = normalizeCarrierKey(row.carrier);
      map.entries.set(`${carrier}:${size}:${eqType}`, row.tare_kg);
    }
    return map;
  }

  lookup(
    carrier: string | null | undefined,
    equipmentSize: string | null | undefined,
    equipmentType: string | null | undefined,
  ): TareLookupResult {
    const size = normalizeSizeBucket(equipmentSize);
    const eqType = normalizeEquipType(equipmentType);

    if (carrier?.trim()) {
      const key = normalizeCarrierKey(carrier);
      const exact = this.entries.get(`${key}:${size}:${eqType}`);
      if (exact != null) return { tareKg: exact, source: "carrier" };
      if (eqType !== FALLBACK_TYPE) {
        const dv = this.entries.get(`${key}:${size}:${FALLBACK_TYPE}`);
        if (dv != null) return { tareKg: dv, source: "carrier" };
      }
    }

    const def =
      this.defaults.get(`${size}:${eqType}`) ??
      this.defaults.get(`${size}:${FALLBACK_TYPE}`) ??
      (size === "20" ? 2200 : eqType === "HC" ? 4000 : 3800);
    return { tareKg: def, source: "default" };
  }
}

/** Sample tare labels for the cargo-weight hint (distinct carriers, same size/type). */
export function formatTareHintSamples(
  map: ContainerTareMap,
  equipmentSize: string | null | undefined,
  equipmentType: string | null | undefined,
  carriers: string[],
  limit = 3,
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const carrier of carriers) {
    const key = normalizeCarrierKey(carrier);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const { tareKg } = map.lookup(carrier, equipmentSize, equipmentType);
    out.push(`${key} ~${Math.round(tareKg).toLocaleString()} kg tare`);
    if (out.length >= limit) break;
  }
  return out;
}
